package com.evoapps.evoshift.viewmodels

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.Notification
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch

data class NotificationsUiState(
    val user: User? = null,
    val items: List<Notification> = emptyList(),
    val isLoading: Boolean = true
) {
    val unreadCount: Int get() = items.count { !it.isRead }
}

/**
 * The signed-in user's notifications (newest first), shared by the bell badge on every screen
 * and the notifications screen. Follows sign-out and account switches.
 */
@OptIn(ExperimentalCoroutinesApi::class)
class NotificationsViewModel(
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val userRepository: UserRepository
) : ViewModel() {

    val uiState: StateFlow<NotificationsUiState> = getCurrentUserUseCase()
        .flatMapLatest { user ->
            if (user == null) flowOf(NotificationsUiState(isLoading = false))
            else userRepository.observeNotifications(user.uid).map { NotificationsUiState(user, it, isLoading = false) }
        }
        .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), NotificationsUiState())

    /** Opening a notification reads it. */
    fun markRead(notification: Notification) {
        if (notification.isRead || notification.id.isEmpty()) return
        viewModelScope.launch {
            val user = getCurrentUserUseCase().first() ?: return@launch
            userRepository.markNotificationsRead(user.uid, listOf(notification.id))
        }
    }

    fun markAllRead() {
        viewModelScope.launch {
            val user = getCurrentUserUseCase().first() ?: return@launch
            val unread = uiState.value.items.filter { !it.isRead && it.id.isNotEmpty() }.map { it.id }
            userRepository.markNotificationsRead(user.uid, unread)
        }
    }
}
